import { useEffect, useRef } from 'react';

import Highcharts from 'highcharts';
import type { HighchartsReactRefObject } from 'highcharts-react-official';
import HighchartsReact from 'highcharts-react-official';

import { categoricalPallete } from '../themes/colors';

type TStackedBarChartOptions = {
  colors?: string[];
  type?: string;
  yAxisTitle?: string;
  xAxisCategories?: string[];
  tooltipFormatter?: (this: any) => string;
  onClick?: (e: any) => void;
  yAxisMax?: number;
  unselect?: boolean;
  height?: string | number;
  yAxisFormatter?: (this: any) => string;
  dataLabelsFormatter?: (this: any) => string;
  legendDisabled?: boolean;
};

export const StackedBarChart = ({
  data,
  chartOptions,
}: {
  data: { name: string; data: number[] }[];
  chartOptions: TStackedBarChartOptions;
}) => {
  const chartRef = useRef<HighchartsReactRefObject>(null);

  const options = {
    chart: {
      type: chartOptions.type ?? 'bar',
      style: {
        fontFamily: 'Inter',
      },
      height: chartOptions.height,
    },
    title: {
      text: '',
      align: 'left',
      style: {
        fontFamily: 'Inter',
      },
    },
    xAxis: {
      categories: chartOptions.xAxisCategories,
      labels: {
        style: {
          fontSize: '12px',
        },
        rotation: 0,
        autoRotation: false,
        enabled: chartOptions.xAxisCategories !== undefined,
      },
      gridLineWidth: 0,
    },
    yAxis: {
      min: 0,
      max: chartOptions.yAxisMax,
      title: {
        text: chartOptions.yAxisTitle,
      },
      gridLineWidth: 0,
      labels: {
        align: 'right',
        formatter: chartOptions.yAxisFormatter,
        step: 2,
        style: {
          fontSize: '12px',
        },
      },
    },
    colors: chartOptions.colors ?? categoricalPallete,
    legend: {
      enabled: !chartOptions.legendDisabled,
      reversed: true,
    },
    plotOptions: {
      series: {
        stacking: 'normal',
        events: {
          click: chartOptions.onClick,
        },
      },
      bar: {
        groupPadding: 0,
        dataLabels: {
          align: 'left',
          enabled: chartOptions.dataLabelsFormatter !== undefined,
          formatter: chartOptions.dataLabelsFormatter,
          style: {
            fontWeight: '400',
            fontSize: '14px',
            fontFamily: 'Inter',
            padding: 1,
          },
          useHTML: true,
        },

        borderRadius: 6,
      },
    },
    tooltip: {
      formatter: chartOptions.tooltipFormatter,
    },
    series: data,
    credits: {
      enabled: false,
    },
  };

  useEffect(() => {
    if (chartOptions.unselect) {
      for (const point of chartRef.current?.chart.getSelectedPoints() ?? []) {
        point.select(false);
      }
    }
  }, [chartOptions.unselect]);

  return (
    <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
  );
};
